/**
 * Service de comptes pour BazarKELY avec pattern offline-first
 * Utilise IndexedDB comme source primaire et Supabase pour la synchronisation
 */

import type { Account } from '../types';
import type { Account as SupabaseAccount, AccountInsert, AccountUpdate } from '../types/supabase';
import type { SyncOperation, SyncPriority } from '../types';
import { SYNC_PRIORITY } from '../types';
import { db } from '../lib/database';
import { supabase, withTimeout } from '../lib/supabase';
import { reconcileStore } from '../lib/syncReconcile';
import { useAppStore } from '../stores/appStore';
import apiService from './apiService';

/**
 * Comptes dont un mouvement de solde est en vol (RPC partie, réponse pas encore
 * revenue). Le rafraîchissement de fond ne doit PAS réécrire leur solde pendant
 * cette fenêtre : il lirait une valeur serveur d'avant le mouvement alors que
 * le delta n'est pas encore dans la file, et écraserait l'affichage local.
 */
const inFlightMovementAccounts = new Map<string, number>();

function markMovementInFlight(accountId: string): void {
  inFlightMovementAccounts.set(accountId, (inFlightMovementAccounts.get(accountId) ?? 0) + 1);
}

function unmarkMovementInFlight(accountId: string): void {
  const count = (inFlightMovementAccounts.get(accountId) ?? 1) - 1;
  if (count <= 0) inFlightMovementAccounts.delete(accountId);
  else inFlightMovementAccounts.set(accountId, count);
}

/** Charge d'un coup la file d'envoi et en extrait ce dont le refresh a besoin. */
async function readSyncQueueState(): Promise<{
  /** Somme des deltas encore en file, par compte (tous statuts). */
  pendingDeltaByAccount: Map<string, number>;
  /** Comptes portant une opération `accounts`/UPDATE encore en attente. */
  pendingAccountUpdateIds: Set<string>;
  /** Budgets portant une opération `budgets` encore en attente. */
  pendingBudgetIds: Set<string>;
}> {
  const pendingDeltaByAccount = new Map<string, number>();
  const pendingAccountUpdateIds = new Set<string>();
  const pendingBudgetIds = new Set<string>();
  try {
    const operations = await db.syncQueue.toArray();
    for (const operation of operations) {
      const data = (operation as any)?.data;
      if (operation.table_name === 'account_balance_movements') {
        const accountId = data?.accountId;
        const delta = Number(data?.delta);
        if (typeof accountId === 'string' && Number.isFinite(delta)) {
          pendingDeltaByAccount.set(accountId, (pendingDeltaByAccount.get(accountId) ?? 0) + delta);
        }
      } else if (operation.table_name === 'accounts' && operation.operation === 'UPDATE') {
        if (typeof data?.id === 'string') pendingAccountUpdateIds.add(data.id);
      } else if (operation.table_name === 'budgets') {
        if (typeof data?.id === 'string') pendingBudgetIds.add(data.id);
      }
    }
  } catch (error) {
    console.warn('⚠️ [AccountService] Lecture de la file de synchronisation impossible:', error);
  }
  return { pendingDeltaByAccount, pendingAccountUpdateIds, pendingBudgetIds };
}

export { readSyncQueueState };

// Timeout par défaut pour les appels Supabase dans les services métier
// Cohérent avec authService et App.tsx (5s)
const SUPABASE_TIMEOUT_MS = 5000;
import { convertAmount } from './exchangeRateService';

class AccountService {
  /**
   * Récupérer l'ID de l'utilisateur actuel — offline-safe.
   * Ordre : 1) useAppStore.user (Zustand, instantané, jamais réseau)
   *        2) supabase.auth.getSession() (lecture localStorage Supabase)
   *        3) null
   * Ne fait JAMAIS supabase.auth.getUser() (fetch réseau, throw en offline).
   */
  private async getCurrentUserId(): Promise<string | null> {
    try {
      const storeUser = useAppStore.getState().user;
      if (storeUser?.id) return storeUser.id;
    } catch {
      /* store pas encore initialisé */
    }
    try {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.user?.id) return data.session.user.id;
    } catch (error) {
      console.error('❌ [AccountService] Erreur lors de la récupération de l\'utilisateur:', error);
    }
    return null;
  }

  /**
   * Queue a sync operation for offline-first processing
   * PWA Phase 3 - Now supports priority, syncTag, expiresAt
   */
  private async queueSyncOperation(
    userId: string,
    operation: 'CREATE' | 'UPDATE' | 'DELETE',
    accountId: string,
    data: any,
    options?: {
      priority?: SyncPriority;
      syncTag?: string;
      expiresAt?: Date | null;
    }
  ): Promise<void> {
    try {
      // Une UPDATE vide n'a plus rien à dire au serveur (cas typique : seul le
      // solde avait changé, et il passe désormais par applyBalanceMovement).
      if (operation === 'UPDATE' && Object.keys(data || {}).length === 0) {
        console.log(`ℹ️ [AccountService] UPDATE vide pour ${accountId}, rien à mettre en file`);
        return;
      }
      const syncOp: SyncOperation = {
        id: crypto.randomUUID(),
        userId,
        operation,
        table_name: 'accounts',
        data: { id: accountId, ...data },
        timestamp: new Date(),
        retryCount: 0,
        status: 'pending',
        // PWA Phase 3 - New optional fields
        priority: options?.priority ?? SYNC_PRIORITY.NORMAL,
        syncTag: options?.syncTag ?? 'bazarkely-sync',
        expiresAt: options?.expiresAt ?? null,
      };
      await db.syncQueue.add(syncOp);
      console.log(`📦 [AccountService] Queued ${operation} operation for account ${accountId} with priority ${syncOp.priority}`);
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout à la queue de synchronisation:', error);
      // Ne pas faire échouer l'opération principale si la queue échoue
    }
  }

  /**
   * Met en file un MOUVEMENT de solde (jamais une valeur absolue).
   * L'id du mouvement est conservé : le serveur ne l'appliquera qu'une seule
   * fois, même si la file le rejoue dix fois (un timeout n'est pas un échec).
   */
  private async queueBalanceMovement(
    userId: string,
    movementId: string,
    accountId: string,
    delta: number,
    kind: 'transaction' | 'ajustement',
    sourceTransactionId?: string
  ): Promise<void> {
    try {
      const syncOp: SyncOperation = {
        id: crypto.randomUUID(),
        userId,
        operation: 'CREATE',
        table_name: 'account_balance_movements',
        data: { id: movementId, accountId, delta, kind, sourceTransactionId: sourceTransactionId ?? null },
        timestamp: new Date(),
        retryCount: 0,
        status: 'pending',
        priority: SYNC_PRIORITY.HIGH,
        syncTag: 'bazarkely-sync',
        expiresAt: null,
      };
      await db.syncQueue.add(syncOp);
      console.log(`📦 [AccountService] Mouvement ${movementId} (${delta}) mis en file pour le compte ${accountId}`);
    } catch (error) {
      console.error('❌ [AccountService] Erreur lors de la mise en file du mouvement de solde:', error);
    }
  }

  /**
   * SEUL point d'entrée pour modifier un solde.
   *
   * L'appareil n'envoie jamais un total : il envoie un mouvement (+X / −X) muni
   * d'un id client. Deux appareils qui bougent le même compte s'additionnent au
   * lieu de s'écraser, et le rejeu d'un envoi « expiré-mais-commité » ne compte
   * qu'une fois (fonction serveur `apply_balance_movement`, idempotente).
   */
  async applyBalanceMovement(
    accountId: string,
    userId: string,
    delta: number,
    opts: { kind: 'transaction' | 'ajustement'; sourceTransactionId?: string }
  ): Promise<Account | null> {
    const movementId = crypto.randomUUID();
    try {
      // STEP 1: appliquer le delta localement tout de suite (affichage instantané,
      // hors ligne compris). C'est la copie locale qui fait foi pour l'écran.
      const existingAccount = await db.accounts.get(accountId);
      if (!existingAccount) {
        console.error(`❌ [AccountService] Compte ${accountId} introuvable en local, mouvement abandonné`);
        return null;
      }
      const localAccount: Account = { ...existingAccount, balance: existingAccount.balance + delta };
      await db.accounts.put(localAccount);
      console.log(
        `💰 [AccountService] Mouvement local ${delta} sur ${accountId}: ${existingAccount.balance} → ${localAccount.balance}`
      );

      // STEP 2: hors ligne → file d'envoi, le même id sera rejoué.
      if (!navigator.onLine) {
        await this.queueBalanceMovement(userId, movementId, accountId, delta, opts.kind, opts.sourceTransactionId);
        return localAccount;
      }

      // STEP 3: en ligne → appel direct de la fonction serveur.
      markMovementInFlight(accountId);
      try {
        const { data, error } = (await withTimeout(
          // La fonction est plus recente que les types generes du projet : cast assume.
          (supabase.rpc as any)('apply_balance_movement', {
            p_id: movementId,
            p_account_id: accountId,
            p_delta: delta,
            p_kind: opts.kind,
            p_source_transaction_id: opts.sourceTransactionId ?? null,
          }) as any,
          SUPABASE_TIMEOUT_MS,
          'accountService.applyBalanceMovement'
        )) as { data: number | null; error: any };

        if (error) throw error;

        // Solde serveur + tout ce qui attend encore de monter pour ce compte.
        const serverBalance = Number(data);
        if (!Number.isFinite(serverBalance)) {
          throw new Error('apply_balance_movement: solde serveur illisible');
        }
        const { pendingDeltaByAccount } = await readSyncQueueState();
        const reconciled: Account = {
          ...localAccount,
          balance: serverBalance + (pendingDeltaByAccount.get(accountId) ?? 0),
        };
        await db.accounts.put(reconciled);
        console.log(`✅ [AccountService] Mouvement appliqué au serveur, solde ${reconciled.balance}`);
        return reconciled;
      } catch (rpcError) {
        // Timeout / réseau / RLS : on met en file. Le serveur a PEUT-ÊTRE déjà
        // appliqué le mouvement — c'est précisément pour cela que l'id est
        // conservé : un rejeu ne le comptera pas deux fois.
        console.warn('⚠️ [AccountService] Mouvement non confirmé par le serveur, mise en file:', rpcError);
        await this.queueBalanceMovement(userId, movementId, accountId, delta, opts.kind, opts.sourceTransactionId);
        return localAccount;
      } finally {
        unmarkMovementInFlight(accountId);
      }
    } catch (error) {
      console.error('❌ [AccountService] Erreur lors de l\'application du mouvement de solde:', error);
      return null;
    }
  }

  /**
   * Rafraîchissement de fond « Supabase → IndexedDB » des comptes.
   * Retour immédiat côté appelant : cette fonction est lancée sans être attendue.
   * Un seul rafraîchissement en vol à la fois (les appels concurrents partagent
   * la même promesse).
   */
  private accountsRefreshInFlight: Promise<void> | null = null;

  async refreshAccountsFromSupabase(userId: string): Promise<void> {
    if (this.accountsRefreshInFlight) return this.accountsRefreshInFlight;
    this.accountsRefreshInFlight = this.doRefreshAccountsFromSupabase(userId).finally(() => {
      this.accountsRefreshInFlight = null;
    });
    return this.accountsRefreshInFlight;
  }

  private async doRefreshAccountsFromSupabase(userId: string): Promise<void> {
    try {
      // Lecture paginée : chaque page porte son propre timeout (pas de
      // withTimeout global, qui tuerait une lecture multi-pages saine).
      const fetchStartedAt = new Date();
      const response = await apiService.getAllAccountsPaged();
      if (!response.success || response.error) {
        console.warn('⚠️ [AccountService] Rafraîchissement de fond échoué:', response.error);
        return;
      }

      const serverAccounts: Account[] = ((response.data as any[]) || [])
        .filter((a: any) => a.user_id === userId)
        .map((a: any) => this.mapSupabaseToAccount(a));

      const { pendingDeltaByAccount, pendingAccountUpdateIds } = await readSyncQueueState();

      const merged: Account[] = [];
      for (const serverAccount of serverAccounts) {
        const local = await db.accounts.get(serverAccount.id);

        // Un mouvement est en vol pour ce compte : la réponse serveur est déjà
        // périmée et le delta n'est pas encore en file. On ne touche pas au solde.
        if (local && inFlightMovementAccounts.has(serverAccount.id)) {
          merged.push({ ...serverAccount, ...local });
          continue;
        }

        const balance = serverAccount.balance + (pendingDeltaByAccount.get(serverAccount.id) ?? 0);

        // Une modification de compte attend encore de monter : ses champs locaux
        // (nom, type…) sont plus récents que ceux du serveur. Le solde, lui,
        // vient toujours du serveur + les mouvements en attente.
        if (local && pendingAccountUpdateIds.has(serverAccount.id)) {
          merged.push({ ...serverAccount, ...local, balance });
        } else {
          merged.push({ ...serverAccount, balance });
        }
      }

      if (merged.length > 0) {
        await db.accounts.bulkPut(merged);
        console.log(`🔄 [AccountService] ${merged.length} compte(s) rafraîchi(s) depuis Supabase (arrière-plan)`);
      }

      // Synchro descendante : retirer du cache local ce que le serveur n'a plus.
      const localAfterFetch = await db.accounts.where('userId').equals(userId).toArray();
      await reconcileStore({
        storeName: 'accounts',
        localRows: localAfterFetch,
        serverIds: serverAccounts.map((a) => a.id),
        fetchStartedAt,
        fetchComplete: response.complete,
        userId,
      });
    } catch (error) {
      console.warn('⚠️ [AccountService] Rafraîchissement de fond échoué (non bloquant):', error);
    }
  }

  /**
   * Convertir un compte Supabase (snake_case) vers Account (camelCase)
   */
  private mapSupabaseToAccount(supabaseAccount: any): Account {
    return {
      id: supabaseAccount.id,
      userId: supabaseAccount.user_id,
      name: supabaseAccount.name,
      type: supabaseAccount.type,
      balance: supabaseAccount.balance,
      currency: supabaseAccount.currency || null, // Support multi-currency: NULL means no preferred currency
      isDefault: supabaseAccount.is_default,
      displayOrder: supabaseAccount.display_order ?? undefined,
      createdAt: new Date(supabaseAccount.created_at)
    };
  }

  /**
   * Récupérer tous les comptes (OFFLINE-FIRST PATTERN)
   * 1. Essaie IndexedDB d'abord (toujours disponible)
   * 2. Si IndexedDB vide et online, fetch depuis Supabase
   * 3. Cache les résultats Supabase dans IndexedDB
   */
  async getAccounts(): Promise<Account[]> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.warn('⚠️ Utilisateur non authentifié, retour des comptes IndexedDB uniquement');
        // Retourner les comptes IndexedDB même sans userId (pour compatibilité)
        const localAccounts = await db.accounts.toArray();
        return localAccounts;
      }

      // STEP 1: Essayer IndexedDB d'abord (offline-first)
      console.log('💾 Récupération des comptes depuis IndexedDB...');
      const localAccounts = await db.accounts
        .where('userId')
        .equals(userId)
        .toArray();

      if (localAccounts.length > 0) {
        console.log(`✅ ${localAccounts.length} compte(s) récupéré(s) depuis IndexedDB`);

        // Rafraîchissement de fond (fire-and-forget) si online : sans lui, un
        // solde corrigé ailleurs ou une suppression serveur ne redescendaient
        // jamais sur cet appareil.
        if (navigator.onLine) {
          this.refreshAccountsFromSupabase(userId).catch((err) => {
            console.warn('⚠️ [AccountService] Refresh background échoué (non bloquant):', err);
          });
        }

        return localAccounts;
      }

      // STEP 2: IndexedDB vide, essayer Supabase si online
      if (!navigator.onLine) {
        console.warn('⚠️ Mode offline et IndexedDB vide, retour d\'un tableau vide');
        return [];
      }

      console.log('🌐 IndexedDB vide, récupération depuis Supabase...');
      // Lecture paginée : chaque page porte son propre timeout (pas de
      // withTimeout global, qui tuerait une lecture multi-pages saine).
      const fetchStartedAt = new Date();
      const response = await apiService.getAllAccountsPaged();
      if (!response.success || response.error) {
        console.error('❌ Erreur lors de la récupération des comptes depuis Supabase:', response.error);
        return [];
      }

      // STEP 3: Mapper et sauvegarder dans IndexedDB
      const supabaseAccounts = (response.data as any[]) || [];
      const accounts: Account[] = supabaseAccounts.map((supabaseAccount: any) =>
        this.mapSupabaseToAccount(supabaseAccount)
      );

      if (accounts.length > 0) {
        // Sauvegarder dans IndexedDB pour la prochaine fois
        try {
          await db.accounts.bulkPut(accounts);
          console.log(`💾 ${accounts.length} compte(s) sauvegardé(s) dans IndexedDB`);
        } catch (idbError) {
          console.error('❌ Erreur lors de la sauvegarde dans IndexedDB:', idbError);
          // Continuer même si la sauvegarde échoue
        }
      }

      // Synchro descendante : retirer du cache local ce que le serveur n'a plus.
      const localAfterFetch = await db.accounts
        .where('userId')
        .equals(userId)
        .toArray();
      await reconcileStore({
        storeName: 'accounts',
        localRows: localAfterFetch,
        serverIds: accounts.map((a) => a.id),
        fetchStartedAt,
        fetchComplete: response.complete,
        userId,
      });

      console.log(`✅ ${accounts.length} compte(s) récupéré(s) depuis Supabase`);
      return accounts;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des comptes:', error);
      // En cas d'erreur, essayer de retourner IndexedDB
      try {
        const userId = await this.getCurrentUserId();
        if (userId) {
          const localAccounts = await db.accounts
            .where('userId')
            .equals(userId)
            .toArray();
          if (localAccounts.length > 0) {
            console.log(`⚠️ Retour de ${localAccounts.length} compte(s) depuis IndexedDB après erreur`);
            return localAccounts;
          }
        }
      } catch (fallbackError) {
        console.error('❌ Erreur lors du fallback IndexedDB:', fallbackError);
      }
      return [];
    }
  }

  async getUserAccounts(_userId: string): Promise<Account[]> {
    try {
      const accounts = await this.getAccounts();
      // Sort by displayOrder ASC, then by createdAt DESC for accounts without displayOrder
      return accounts.sort((a, b) => {
        const aOrder = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
        const bOrder = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        // If same displayOrder or both null, sort by createdAt DESC
        return b.createdAt.getTime() - a.createdAt.getTime();
      });
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des comptes utilisateur:', error);
      return [];
    }
  }

  /**
   * Récupérer un compte par ID
   */
  async getAccount(id: string, _userId?: string): Promise<Account | null> {
    try {
      // Pour l'instant, on récupère tous les comptes et on filtre
      const accounts = await this.getAccounts();
      return accounts.find(account => account.id === id) || null;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du compte:', error);
      return null;
    }
  }

  /**
   * Créer un nouveau compte (OFFLINE-FIRST PATTERN)
   * 1. Génère un UUID si non fourni
   * 2. Sauvegarde dans IndexedDB immédiatement
   * 3. Si online, sync vers Supabase
   * 4. Si offline ou échec, queue pour sync ultérieure
   */
  async createAccount(userId: string, accountData: Omit<Account, 'id' | 'createdAt' | 'userId'>): Promise<Account | null> {
    try {
      // Générer un UUID pour le compte
      const accountId = crypto.randomUUID();
      const now = new Date();

      // Créer l'objet Account complet
      // Support multi-currency: currency is optional (NULL means no preferred currency)
      const account: Account = {
        id: accountId,
        userId,
        name: accountData.name,
        type: accountData.type,
        balance: accountData.balance,
        currency: accountData.currency ?? null, // Default to null for multi-currency support
        isDefault: accountData.isDefault ?? false,
        displayOrder: accountData.displayOrder,
        createdAt: now
      };

      // STEP 1: Sauvegarder dans IndexedDB immédiatement (offline-first)
      console.log('💾 Sauvegarde du compte dans IndexedDB...');
      await db.accounts.add(account);
      console.log(`✅ Compte "${account.name}" sauvegardé dans IndexedDB avec ID: ${accountId}`);

      // STEP 2: Si online, essayer de sync vers Supabase
      if (navigator.onLine) {
        try {
          console.log('🌐 Synchronisation du compte vers Supabase...');
          const supabaseData: AccountInsert = {
            name: accountData.name,
            type: accountData.type,
            balance: accountData.balance,
            currency: accountData.currency ?? null, // Support multi-currency: NULL means no preferred currency
            is_default: accountData.isDefault ?? false,
            is_active: (accountData as any).isActive ?? true // isActive existe dans Supabase mais pas dans le type Account local
          };
          if (accountData.displayOrder !== undefined) {
            (supabaseData as any).display_order = accountData.displayOrder;
          }
          (supabaseData as any).id = accountId; // Idempotence : même id que IndexedDB → upsert évite les doublons

          const response = await withTimeout(
            apiService.createAccount(supabaseData),
            SUPABASE_TIMEOUT_MS,
            'accountService.createAccount'
          );
          if (response.success && response.data) {
            // Mettre à jour IndexedDB avec l'ID du serveur si différent
            const supabaseAccount = response.data as any;
            if (supabaseAccount.id !== accountId) {
              // Si Supabase génère un ID différent, mettre à jour IndexedDB
              await db.accounts.delete(accountId);
              const syncedAccount = this.mapSupabaseToAccount(supabaseAccount);
              await db.accounts.add(syncedAccount);
              console.log(`🔄 ID du compte mis à jour: ${accountId} → ${syncedAccount.id}`);
              return syncedAccount;
            }
            console.log('✅ Compte synchronisé avec Supabase');
            return account;
          } else {
            console.warn('⚠️ Échec de la synchronisation Supabase, ajout à la queue');
            // Queue pour sync ultérieure
            await this.queueSyncOperation(userId, 'CREATE', accountId, accountData);
            return account;
          }
        } catch (syncError) {
          console.error('❌ Erreur lors de la synchronisation Supabase:', syncError);
          // Queue pour sync ultérieure
          await this.queueSyncOperation(userId, 'CREATE', accountId, accountData);
          return account;
        }
      } else {
        // Mode offline, queue pour sync ultérieure
        console.log('📦 Mode offline, ajout à la queue de synchronisation');
        await this.queueSyncOperation(userId, 'CREATE', accountId, accountData);
        return account;
      }
    } catch (error) {
      console.error('❌ Erreur lors de la création du compte:', error);
      return null;
    }
  }

  /**
   * Mettre à jour un compte (OFFLINE-FIRST PATTERN)
   * 1. Met à jour IndexedDB immédiatement
   * 2. Si online, sync vers Supabase
   * 3. Si offline, queue pour sync ultérieure
   */
  async updateAccount(id: string, userId: string, accountData: Partial<Omit<Account, 'id' | 'createdAt' | 'userId'>>): Promise<Account | null> {
    try {
      // STEP 1: Récupérer le compte depuis IndexedDB
      const existingAccount = await db.accounts.get(id);
      if (!existingAccount) {
        console.error(`❌ Compte ${id} non trouvé dans IndexedDB`);
        // Essayer de récupérer depuis Supabase si online
        if (navigator.onLine) {
          const accounts = await this.getAccounts();
          const account = accounts.find(a => a.id === id);
          if (account) {
            // Mettre à jour avec les nouvelles données
            const updatedAccount = { ...account, ...accountData };
            await db.accounts.put(updatedAccount);
            return updatedAccount;
          }
        }
        return null;
      }

      // Un solde ne se met JAMAIS à jour par une valeur absolue : le dernier
      // appareil qui écrit écraserait les mouvements des autres. Le champ est
      // ignoré côté serveur ET côté file — voir applyBalanceMovement().
      if (accountData.balance !== undefined) {
        console.warn('⚠️ updateAccount: balance ignored, use applyBalanceMovement');
      }
      const { balance: _ignoredBalance, ...syncableData } = accountData;

      // STEP 2: Mettre à jour IndexedDB immédiatement
      const updatedAccount: Account = {
        ...existingAccount,
        ...accountData
      };
      console.log('💾 Mise à jour du compte dans IndexedDB...');
      await db.accounts.put(updatedAccount);
      console.log(`✅ Compte "${updatedAccount.name}" mis à jour dans IndexedDB`);

      // STEP 3: Si online, essayer de sync vers Supabase
      if (navigator.onLine) {
        try {
          console.log('🌐 Synchronisation de la mise à jour vers Supabase...');
          const supabaseData: AccountUpdate = {};
          if (accountData.name !== undefined) supabaseData.name = accountData.name;
          if (accountData.type !== undefined) supabaseData.type = accountData.type;
          // `balance` volontairement absent : jamais de valeur absolue.
          // Support multi-currency: allow setting currency to null explicitly
          if (accountData.currency !== undefined) {
            supabaseData.currency = accountData.currency ?? null;
          }
          if (accountData.isDefault !== undefined) supabaseData.is_default = accountData.isDefault;
          // isActive existe dans Supabase mais pas dans le type Account local
          if ((accountData as any).isActive !== undefined) supabaseData.is_active = (accountData as any).isActive;
          if (accountData.displayOrder !== undefined) supabaseData.display_order = accountData.displayOrder;

          // Rien d'autre que le solde n'a changé : il n'y a plus rien à envoyer.
          if (Object.keys(supabaseData).length === 0) {
            return updatedAccount;
          }

          const response = await withTimeout(
            apiService.updateAccount(id, supabaseData),
            SUPABASE_TIMEOUT_MS,
            'accountService.updateAccount'
          );
          if (response.success && response.data) {
            // Mettre à jour IndexedDB avec les données Supabase (pour synchronisation),
            // MAIS garder le solde local : il n'appartient qu'aux mouvements, et la
            // réponse serveur peut précéder un mouvement encore en file.
            const supabaseAccount = this.mapSupabaseToAccount(response.data as any);
            const synced: Account = { ...supabaseAccount, balance: updatedAccount.balance };
            await db.accounts.put(synced);
            console.log('✅ Compte synchronisé avec Supabase');
            return synced;
          } else {
            // Supabase retourne 0 lignes (RLS policy ou ligne manquante)
            // Ne pas bloquer - l'update IndexedDB a réussi, continuer avec le compte local
            console.warn('⚠️ Échec de la synchronisation Supabase (RLS ou ligne manquante), utilisation du compte IndexedDB');
            await this.queueSyncOperation(userId, 'UPDATE', id, syncableData);
            return updatedAccount;
          }
        } catch (syncError) {
          // Erreur Supabase ne doit pas bloquer - l'update IndexedDB a réussi
          console.warn('⚠️ Erreur lors de la synchronisation Supabase (non-bloquant):', syncError);
          await this.queueSyncOperation(userId, 'UPDATE', id, syncableData);
          return updatedAccount;
        }
      } else {
        // Mode offline, queue pour sync ultérieure
        console.log('📦 Mode offline, ajout à la queue de synchronisation');
        await this.queueSyncOperation(userId, 'UPDATE', id, syncableData);
        return updatedAccount;
      }
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du compte:', error);
      return null;
    }
  }

  /**
   * Mettre à jour l'ordre des comptes pour le drag-and-drop
   * @param userId - ID de l'utilisateur
   * @param orderedAccountIds - Tableau d'IDs de comptes dans l'ordre souhaité
   * @returns true si la mise à jour réussit, false sinon
   */
  async updateAccountsOrder(userId: string, orderedAccountIds: string[]): Promise<boolean> {
    try {
      console.log('🔄 Updating accounts order for user:', userId);
      
      // Mettre à jour chaque compte avec son nouvel ordre (index + 1)
      const updatePromises = orderedAccountIds.map(async (accountId, index) => {
        const displayOrder = index + 1;
        try {
          const result = await this.updateAccount(accountId, userId, { displayOrder });
          if (!result) {
            console.error(`❌ Failed to update order for account ${accountId}`);
            return false;
          }
          return true;
        } catch (error) {
          console.error(`❌ Error updating order for account ${accountId}:`, error);
          return false;
        }
      });

      const results = await Promise.all(updatePromises);
      const allSuccess = results.every(result => result === true);

      if (allSuccess) {
        console.log('✅ Accounts order updated successfully');
        return true;
      } else {
        console.error('❌ Some accounts failed to update order');
        return false;
      }
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour de l\'ordre des comptes:', error);
      return false;
    }
  }

  /**
   * Supprimer un compte (OFFLINE-FIRST PATTERN)
   * 1. Supprime de IndexedDB immédiatement
   * 2. Si online, sync suppression vers Supabase
   * 3. Si offline, queue pour sync ultérieure
   */
  async deleteAccount(id: string, userId?: string): Promise<boolean> {
    try {
      // STEP 1: Récupérer le userId si non fourni
      const currentUserId = userId || await this.getCurrentUserId();
      if (!currentUserId) {
        console.error('❌ Utilisateur non authentifié');
        return false;
      }

      // STEP 2: Récupérer le compte depuis IndexedDB pour la queue
      const account = await db.accounts.get(id);
      if (!account) {
        console.warn(`⚠️ Compte ${id} non trouvé dans IndexedDB`);
        // Essayer quand même de supprimer depuis Supabase si online
        if (navigator.onLine) {
          try {
            const response = await withTimeout(
              apiService.deleteAccount(id),
              SUPABASE_TIMEOUT_MS,
              'accountService.deleteAccount/orphan'
            );
            return response.success;
          } catch (error) {
            console.warn('⚠️ Échec/timeout suppression Supabase (orphan):', error);
            return false;
          }
        }
        return false;
      }

      // STEP 3: Supprimer de IndexedDB immédiatement
      console.log('💾 Suppression du compte depuis IndexedDB...');
      await db.accounts.delete(id);
      console.log(`✅ Compte "${account.name}" supprimé de IndexedDB`);

      // STEP 4: Si online, essayer de sync vers Supabase
      if (navigator.onLine) {
        try {
          console.log('🌐 Synchronisation de la suppression vers Supabase...');
          const response = await withTimeout(
            apiService.deleteAccount(id),
            SUPABASE_TIMEOUT_MS,
            'accountService.deleteAccount'
          );
          if (response.success) {
            console.log('✅ Suppression synchronisée avec Supabase');
            return true;
          } else {
            console.warn('⚠️ Échec de la synchronisation Supabase, ajout à la queue');
            await this.queueSyncOperation(currentUserId, 'DELETE', id, {});
            return true; // Retourner true car supprimé de IndexedDB
          }
        } catch (syncError) {
          console.error('❌ Erreur lors de la synchronisation Supabase:', syncError);
          await this.queueSyncOperation(currentUserId, 'DELETE', id, {});
          return true; // Retourner true car supprimé de IndexedDB
        }
      } else {
        // Mode offline, queue pour sync ultérieure
        console.log('📦 Mode offline, ajout à la queue de synchronisation');
        await this.queueSyncOperation(currentUserId, 'DELETE', id, {});
        return true; // Retourner true car supprimé de IndexedDB
      }
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du compte:', error);
      return false;
    }
  }

  /**
   * Définir un compte comme par défaut
   */
  async setDefaultAccount(accountId: string, userId?: string): Promise<boolean> {
    try {
      // D'abord, retirer le statut par défaut de tous les comptes
      const accounts = await this.getAccounts();
      for (const account of accounts) {
        if (account.isDefault) {
          await this.updateAccount(account.id, userId || '', { isDefault: false });
        }
      }

      // Ensuite, définir le compte sélectionné comme par défaut
      const success = await this.updateAccount(accountId, userId || '', { isDefault: true });
      
      if (success) {
        console.log('✅ Compte défini comme par défaut');
        return true;
      }
      
      return !!success;
    } catch (error) {
      console.error('❌ Erreur lors de la définition du compte par défaut:', error);
      return false;
    }
  }

  /**
   * Obtenir le compte par défaut
   */
  async getDefaultAccount(): Promise<Account | null> {
    try {
      const accounts = await this.getAccounts();
      return accounts.find(account => account.isDefault) || null;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du compte par défaut:', error);
      return null;
    }
  }

  /**
   * Obtenir les comptes par type
   */
  async getAccountsByType(type: string): Promise<Account[]> {
    try {
      const accounts = await this.getAccounts();
      return accounts.filter(account => account.type === type);
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des comptes par type:', error);
      return [];
    }
  }

  /**
   * Calculer le solde total
   * Note: Pour un total avec conversion, utiliser getTotalBalanceInCurrency()
   */
  async getTotalBalance(): Promise<number> {
    try {
      const accounts = await this.getAccounts();
      return accounts.reduce((total, account) => total + account.balance, 0);
    } catch (error) {
      console.error('❌ Erreur lors du calcul du solde total:', error);
      return 0;
    }
  }

  /**
   * Initialiser et nettoyer les comptes
   */
  async initializeAndCleanup(): Promise<void> {
    try {
      console.log('🔍 Initialisation des comptes...');
      
      // Vérifier s'il y a un compte Espèces par défaut
      const especesAccounts = await this.getAccountsByType('especes');
      
      if (especesAccounts.length === 0) {
        // Créer un compte Espèces par défaut
        await this.createAccount('', {
          name: 'Espèces',
          type: 'especes',
          balance: 0,
          isDefault: true,
          currency: 'MGA' as const
        });
        console.log('✅ Compte Espèces par défaut créé');
      } else if (!especesAccounts.some(acc => acc.isDefault)) {
        // S'assurer qu'un compte Espèces est marqué comme par défaut
        await this.setDefaultAccount(especesAccounts[0].id);
        console.log('✅ Compte Espèces marqué comme par défaut');
      } else {
        console.log('ℹ️ Compte Espèces par défaut déjà existant');
      }
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation des comptes:', error);
    }
  }
}

const accountService = new AccountService();

/**
 * Calcule le solde total de tous les comptes, converti dans la devise cible
 * @param targetCurrency - Devise cible ('MGA' ou 'EUR')
 * @returns Solde total dans la devise cible
 */
export async function getTotalBalanceInCurrency(targetCurrency: 'MGA' | 'EUR' = 'MGA'): Promise<number> {
  try {
    const accounts = await accountService.getAccounts();
    let total = 0;
    
    for (const account of accounts) {
      // Support multi-currency: if account has no preferred currency, assume MGA
      const accountCurrency = account.currency || 'MGA';
      
      if (accountCurrency === targetCurrency) {
        // Même devise, pas de conversion
        total += account.balance;
      } else {
        // Conversion nécessaire
        const converted = await convertAmount(
          account.balance,
          accountCurrency,
          targetCurrency
        );
        total += converted;
      }
    }
    
    // Arrondir selon la devise
    return targetCurrency === 'MGA' ? Math.round(total) : Math.round(total * 100) / 100;
  } catch (error) {
    console.error('Error calculating total balance:', error);
    return 0;
  }
}

export default accountService;